/**
 * Shared production-shaped chrome for ?test= continuity prototypes.
 * Same framework as Flows Studio: header, icon rail, tab row, title row.
 * Secondary panels must sit BELOW ProtoTitleRow, beside content.
 */

import { useMemo, useState, type MouseEvent, type ReactNode } from 'react'
import {
  AlignJustify,
  Bot,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  HelpCircle,
  Home,
  Inbox,
  List,
  MoreVertical,
  PanelLeft,
  Plus,
  Search,
  Settings,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome'
import { Button } from '@/components/ui/button'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { cn } from '@/lib/utils'
import {
  SHELL_APP_HEADER_CLASS,
  SHELL_COLUMN_HEADER_CLASS,
  SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS,
  SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
  SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
  SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
  SHELL_ICON_RAIL_BUTTON_SUGGESTED_PULSE_CLASS,
  SHELL_ICON_RAIL_CLASS,
} from '@/lib/shell-chrome'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import {
  MaaterProtoSegmentTrack,
  MAATER_APP_THEME_OPTIONS,
  MAATER_PROTO_THEME_OPTIONS,
  type MaaterAppThemeId,
  type MaaterProtoThemeId,
} from '@/components/prototype/maater-proto-shell'

export type ProtoAppThemeControl = 'paper-ink-stone' | 'light-dark' | 'none'

export type ProtoCrumb = { id: string; label: string }

export function normalizeProtoCrumbs(
  crumbs: (string | ProtoCrumb)[],
): ProtoCrumb[] {
  return crumbs.map((c, i) =>
    typeof c === 'string' ? { id: `crumb-${i}`, label: c } : c,
  )
}

export function ProtoSurfaceBreadcrumb({
  crumbs,
  onCrumb,
}: {
  crumbs: (string | ProtoCrumb)[]
  onCrumb?: (id: string) => void
}) {
  const normalized = normalizeProtoCrumbs(crumbs)
  return (
    <nav className="flex min-w-0 items-center gap-1 text-xs" aria-label="Breadcrumb">
      {normalized.map((c, i) => {
        const last = i === normalized.length - 1
        return (
          <span key={c.id} className="inline-flex min-w-0 items-center gap-1">
            {i > 0 && (
              <ChevronRight
                className="h-3 w-3 shrink-0"
                style={{ color: 'var(--ink-faint)' }}
              />
            )}
            {last || !onCrumb ? (
              <span
                className={cn('truncate', last && 'font-medium')}
                style={{ color: last ? 'var(--ink)' : 'var(--ink-soft)' }}
              >
                {c.label}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onCrumb(c.id)}
                className="truncate hover:underline"
                style={{ color: 'var(--ink-soft)' }}
              >
                {c.label}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}

export function ProtoAppHeader({
  theme,
  onTheme,
  appTheme,
  onAppTheme,
  themeControl = 'paper-ink-stone',
  badge = 'proto',
  prototypeIndexTestId,
  testId,
}: {
  /** Required when themeControl is paper-ink-stone */
  theme?: MaaterProtoThemeId
  onTheme?: (t: MaaterProtoThemeId) => void
  /** Required when themeControl is light-dark */
  appTheme?: MaaterAppThemeId
  onAppTheme?: (t: MaaterAppThemeId) => void
  themeControl?: ProtoAppThemeControl
  badge?: string
  prototypeIndexTestId?: string
  testId?: string
}) {
  return (
    <header
      className={cn(
        'flex shrink-0 items-center gap-3 border-b px-3',
        SHELL_APP_HEADER_CLASS,
      )}
      style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
      data-testid={testId ?? 'maater-proto-app-header'}
    >
      <div className="flex w-[200px] shrink-0 items-center gap-2">
        <span
          className="text-sm font-semibold tracking-[0.12em]"
          style={{
            fontFamily: 'Karla, ui-sans-serif, system-ui, sans-serif',
            color: 'var(--ink)',
          }}
        >
          M<span style={{ color: 'var(--rust)' }}>AA</span>TER
        </span>
        <span
          className="rounded px-1 py-px text-[9px] font-medium"
          style={{
            background: 'var(--rust-pale)',
            color: 'var(--rust)',
            border: '1px solid color-mix(in oklch, var(--rust) 30%, transparent)',
          }}
        >
          {badge}
        </span>
      </div>
      <button
        type="button"
        className="mx-auto flex h-8 w-full max-w-xl items-center gap-2 rounded-md border px-3 text-left text-xs"
        style={{
          borderColor: 'var(--line)',
          background: 'var(--paper)',
          color: 'var(--ink-faint)',
        }}
      >
        <Search className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1 truncate">Search... (⇧⌘F)</span>
        <kbd
          className="rounded border px-1.5 py-0.5 text-[10px]"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-soft)' }}
        >
          ⌘F
        </kbd>
      </button>
      <div className="flex shrink-0 items-center gap-1.5">
        {themeControl === 'paper-ink-stone' && theme && onTheme ? (
          <MaaterProtoSegmentTrack
            value={theme}
            onChange={(id) => onTheme(id as MaaterProtoThemeId)}
            options={MAATER_PROTO_THEME_OPTIONS}
            className="hidden md:inline-flex"
          />
        ) : null}
        {themeControl === 'light-dark' && appTheme && onAppTheme ? (
          <MaaterProtoSegmentTrack
            value={appTheme}
            onChange={(id) => onAppTheme(id as MaaterAppThemeId)}
            options={MAATER_APP_THEME_OPTIONS}
            className="hidden md:inline-flex"
          />
        ) : null}
        <span className="hidden text-xs sm:inline" style={{ color: 'var(--ink-soft)' }}>
          Rowan
        </span>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md"
          style={{ color: 'var(--ink-soft)' }}
          title="Settings"
        >
          <Settings className="h-4 w-4" />
        </button>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-muted-foreground" asChild>
          <a href="/?test=index" data-testid={prototypeIndexTestId}>
            Prototypes
          </a>
        </Button>
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md"
          style={{ color: 'var(--ink-soft)' }}
          title="Help"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </div>
    </header>
  )
}

export type ProtoIconRailActive = 'home' | 'inbox' | 'documents' | 'flows' | 'agent' | 'basecamp'

export function ProtoIconRail({
  active = 'flows',
  suggested = null,
  inboxBadge = 4,
  testId,
  onSelect,
}: {
  active?: ProtoIconRailActive
  suggested?: ProtoIconRailActive | null
  inboxBadge?: number
  testId?: string
  onSelect?: (id: ProtoIconRailActive) => void
}) {
  const items = [
    { id: 'collapse' as const, icon: <PanelLeft className="h-4 w-4" />, label: 'Collapse' },
    { id: 'home' as const, icon: <Home className="h-4 w-4" />, label: 'Home' },
    {
      id: 'inbox' as const,
      icon: <Inbox className="h-4 w-4" />,
      label: 'Inbox',
      badge: inboxBadge,
    },
    { id: 'documents' as const, icon: <FileText className="h-4 w-4" />, label: 'Browse' },
    {
      id: 'basecamp' as const,
      icon: <BasecampIcon className="h-4 w-4" />,
      label: 'Basecamp',
    },
    { id: 'flows' as const, icon: <Zap className="h-4 w-4" />, label: 'Flows' },
    { id: 'agent' as const, icon: <Bot className="h-4 w-4" />, label: 'Agent' },
  ]
  return (
    <aside
      className={cn(
        'flex shrink-0 flex-col items-center gap-1 border-r py-2',
        SHELL_ICON_RAIL_CLASS,
      )}
      style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
      data-testid={testId ?? 'maater-proto-icon-rail'}
    >
      {items.map((item) => {
        const isActive = item.id === active
        const isSuggested = Boolean(suggested && item.id === suggested && !isActive)
        const label =
          isSuggested && item.id === 'basecamp' ? 'Show Basecamp' : item.label
        return (
          <button
            key={item.id}
            type="button"
            title={label}
            aria-label={label}
            data-suggested={isSuggested ? 'true' : undefined}
            data-testid={`maater-proto-rail-${item.id}`}
            onClick={() => {
              if (item.id === 'collapse' || !onSelect) return
              onSelect(item.id)
            }}
            className={cn(
              SHELL_ICON_RAIL_BUTTON_BASE_CLASS,
              isActive
                ? SHELL_ICON_RAIL_BUTTON_ACTIVE_CLASS
                : isSuggested
                  ? cn(
                      SHELL_ICON_RAIL_BUTTON_SUGGESTED_CLASS,
                      SHELL_ICON_RAIL_BUTTON_SUGGESTED_PULSE_CLASS,
                    )
                  : SHELL_ICON_RAIL_BUTTON_IDLE_CLASS,
            )}
          >
            {item.icon}
            {'badge' in item && item.badge ? (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[9px] font-semibold text-white"
                style={{ background: 'oklch(55% 0.18 250)' }}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        )
      })}
    </aside>
  )
}

export function ProtoTabRow({
  tabs,
  activeId,
  onSelect,
  onTabContextMenu,
  testId,
}: {
  tabs: { id: string; label: string; icon?: ReactNode; testId?: string }[]
  activeId: string
  onSelect: (id: string) => void
  /** Right-click on a tab. Scenes pass the production tab menu (ADR-0020). */
  onTabContextMenu?: (id: string, event: MouseEvent) => void
  testId?: string
}) {
  return (
    <div
      className={cn(
        'document-tabs-bar relative flex w-full shrink-0 items-end overflow-hidden border-b border-border px-2',
        SHELL_COLUMN_HEADER_CLASS,
      )}
      style={{ backgroundColor: 'hsl(var(--tab-bar-bg))' }}
      data-testid={testId ?? 'maater-proto-tab-row'}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelect(tab.id)}
            onContextMenu={onTabContextMenu ? (event) => onTabContextMenu(tab.id, event) : undefined}
            data-testid={tab.testId}
            className={cn(
              'tab-group__tab group flex max-w-[180px] items-center gap-1.5 px-2 py-1.5 min-w-0 flex-shrink-0 relative rounded-t-lg text-xs',
              isActive && 'tab-group__tab--active text-[hsl(var(--tab-active-text))]',
              !isActive && 'text-[hsl(var(--tab-inactive-text))]',
            )}
          >
            {tab.icon ? (
              <span className="flex shrink-0 items-center">{tab.icon}</span>
            ) : null}
            <span className="truncate">{tab.label}</span>
          </button>
        )
      })}
      <button
        type="button"
        className="mb-0.5 flex h-7 w-7 items-center justify-center rounded-md text-[hsl(var(--tab-inactive-text))]"
        title="New tab"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

/**
 * Title / address row - full width of main column.
 * Secondary panels must render BELOW this row (sibling of content).
 *
 * density:
 * - `stacked` (default): crumbs + h1 title (+ optional subtitle) - list / index scenes
 * - `inline`: single reader chrome row `[ < > | crumbs…/title | actions ]` - no duplicate h1
 *   (see docs/design/maater-list-surface-continuity.md Reader chrome)
 */
export function ProtoTitleRow({
  crumbs,
  onCrumb,
  title,
  subtitle,
  canGoBack,
  canGoForward,
  onBack,
  onForward,
  actions,
  testId,
  backTestId,
  forwardTestId,
  density = 'stacked',
}: {
  crumbs: (string | ProtoCrumb)[]
  onCrumb?: (id: string) => void
  title: string
  subtitle?: ReactNode
  canGoBack?: boolean
  canGoForward?: boolean
  onBack?: () => void
  onForward?: () => void
  actions?: ReactNode
  testId?: string
  backTestId?: string
  forwardTestId?: string
  density?: 'stacked' | 'inline'
}) {
  if (density === 'inline') {
    const inlineCrumbs = normalizeProtoCrumbs(crumbs)
    const hasTitleCrumb = inlineCrumbs.some(
      (c) => c.label === title || c.id === 'thread',
    )
    const trail = hasTitleCrumb
      ? inlineCrumbs
      : [...inlineCrumbs, { id: 'thread', label: title }]
    return (
      <div
        className="flex shrink-0 items-center gap-2 border-b px-3 py-2"
        style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
        data-testid={testId ?? 'maater-proto-title-row'}
      >
        <NavigationButtons
          canGoBack={Boolean(canGoBack)}
          canGoForward={Boolean(canGoForward)}
          onGoBack={onBack}
          onGoForward={onForward}
          backTestId={backTestId}
          forwardTestId={forwardTestId}
        />
        <div className="min-w-0 flex-1">
          <ProtoSurfaceBreadcrumb crumbs={trail} onCrumb={onCrumb} />
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-1.5">{actions}</div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className="flex shrink-0 items-start gap-2 border-b px-3 py-2.5"
      style={{ borderColor: 'var(--line)', background: 'var(--paper)' }}
      data-testid={testId ?? 'maater-proto-title-row'}
    >
      <NavigationButtons
        canGoBack={Boolean(canGoBack)}
        canGoForward={Boolean(canGoForward)}
        onGoBack={onBack}
        onGoForward={onForward}
        backTestId={backTestId}
        forwardTestId={forwardTestId}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <ProtoSurfaceBreadcrumb crumbs={crumbs} onCrumb={onCrumb} />
        <h1
          className="truncate text-lg font-semibold leading-tight tracking-tight"
          style={{ fontFamily: 'Fraunces, Georgia, serif', color: 'var(--ink)' }}
        >
          {title}
        </h1>
        {subtitle ? (
          <div className="text-xs" style={{ color: 'var(--ink-soft)' }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1.5 pt-0.5">{actions}</div> : null}
    </div>
  )
}

/** Mode panel shell (Flows nav / Inbox folders) - paperRaised rail. */
export function ProtoModePanel({
  title,
  subtitle,
  children,
  testId,
  footer,
  density,
  onDensityChange,
}: {
  title: string
  /** Optional hint under the title (e.g. gallery-only scene nav) */
  subtitle?: string
  children: ReactNode
  testId?: string
  footer?: ReactNode
  /** Detailed (false) or Dense (true). Uncontrolled when omitted. */
  density?: boolean
  onDensityChange?: (dense: boolean) => void
}) {
  const [localDense, setLocalDense] = useState(false)
  const dense = density ?? localDense
  const setDense = onDensityChange ?? setLocalDense
  return (
    <aside
      className="flex w-[220px] shrink-0 flex-col overflow-hidden border-r"
      style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
      data-testid={testId}
      data-sidebar-density={dense ? 'dense' : 'detailed'}
    >
      <div
        className="flex shrink-0 flex-col gap-0.5 border-b px-3 py-2"
        style={{ borderColor: 'var(--line)' }}
      >
        <div className="flex items-center gap-2">
          <p className="flex-1 text-sm font-semibold" style={{ color: 'var(--ink)' }}>
            {title}
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Sidebar options"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
              className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
            >
              <DropdownMenuItem onSelect={() => setDense(false)}>
                <AlignJustify className="h-4 w-4" />
                Detailed layout
                {!dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setDense(true)}>
                <List className="h-4 w-4" />
                Dense layout
                {dense ? <Check className="ml-auto h-3.5 w-3.5" /> : null}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {subtitle ? (
          <p className="text-[10px] leading-snug" style={{ color: 'var(--ink-faint)' }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">{children}</div>
      {footer ? (
        <div className="shrink-0 border-t px-2 py-2" style={{ borderColor: 'var(--line)' }}>
          {footer}
        </div>
      ) : null}
    </aside>
  )
}

/**
 * Vercel-style stack / drill-down mode nav (not expand/collapse trees).
 * Enter a parent: list is replaced; header becomes centered title + back chevron.
 * Gallery: /?test=maater-ui#mode-panel-stack
 * Guide: docs/design/maater-list-surface-continuity.md (Mode panel depth)
 */

export type ProtoStackNavBadge = { label: string; tone?: 'muted' | 'info' }

export type ProtoStackNavNode = {
  id: string
  label: string
  icon?: LucideIcon
  badge?: ProtoStackNavBadge
  /** Uppercase section label rendered above this node */
  section?: string
  children?: ProtoStackNavNode[]
}

export type ProtoStackNavProps = {
  items: ProtoStackNavNode[]
  activeId?: string | null
  onSelectLeaf?: (id: string, trail: { id: string; label: string }[]) => void
  testId?: string
  className?: string
  /** Root panel label when stack is empty (optional; omit for root-only list) */
  rootTitle?: string
}

function findNode(
  nodes: ProtoStackNavNode[],
  id: string,
): ProtoStackNavNode | null {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children) {
      const hit = findNode(n.children, id)
      if (hit) return hit
    }
  }
  return null
}

export function ProtoStackNav({
  items,
  activeId = null,
  onSelectLeaf,
  testId,
  className,
}: ProtoStackNavProps) {
  const [stack, setStack] = useState<{ id: string; label: string }[]>([])

  const currentItems = useMemo(() => {
    if (stack.length === 0) return items
    let level = items
    for (const frame of stack) {
      const node = level.find((n) => n.id === frame.id)
      if (!node?.children) return level
      level = node.children
    }
    return level
  }, [items, stack])

  const title = stack.length > 0 ? stack[stack.length - 1]!.label : null

  const goBack = () => setStack((s) => s.slice(0, -1))

  const activate = (node: ProtoStackNavNode) => {
    if (node.children && node.children.length > 0) {
      setStack((s) => [...s, { id: node.id, label: node.label }])
      return
    }
    const trail = [...stack, { id: node.id, label: node.label }]
    onSelectLeaf?.(node.id, trail)
  }

  /** At root only: highlight the parent that owns the active leaf. */
  const shellActiveParent = useMemo(() => {
    if (!activeId || stack.length > 0) return null
    for (const n of items) {
      if (n.children && findNode(n.children, activeId)) return n.id
    }
    return null
  }, [activeId, stack.length, items])

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col', className)}
      data-testid={testId ?? 'maater-proto-stack-nav'}
    >
      {title ? (
        <div className="shrink-0 px-2 pb-1">
          <button
            type="button"
            onClick={goBack}
            className="flex h-9 w-full items-center gap-1 rounded-md transition-colors"
            style={{ color: 'var(--ink-soft)' }}
            data-testid={`${testId ?? 'maater-proto-stack-nav'}-back`}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--paper)'
              e.currentTarget.style.color = 'var(--ink)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--ink-soft)'
            }}
          >
            <span className="grid size-9 shrink-0 place-content-center">
              <ChevronLeft className="h-4 w-4" />
            </span>
            <span
              className="min-w-0 flex-1 truncate text-center text-[13px] font-medium"
              style={{ color: 'var(--ink)' }}
            >
              {title}
            </span>
            <span className="size-9 shrink-0" aria-hidden />
          </button>
        </div>
      ) : null}

      <nav className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto px-2 pb-2">
        {currentItems.map((node) => {
          const Icon = node.icon
          const hasChildren = Boolean(node.children?.length)
          const selected =
            activeId === node.id || shellActiveParent === node.id

          return (
            <div key={node.id}>
              {node.section ? (
                <div
                  className="px-2 pb-0.5 pt-3 text-[11px] font-medium uppercase tracking-[0.04em]"
                  style={{ color: 'var(--ink-faint)' }}
                >
                  {node.section}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => activate(node)}
                className="group flex h-9 w-full origin-left items-center rounded-md text-left transition-colors"
                style={
                  selected
                    ? {
                        background: 'var(--paper-deep)',
                        color: 'var(--ink)',
                      }
                    : {
                        background: 'transparent',
                        color: 'var(--ink-soft)',
                      }
                }
                data-testid={`${testId ?? 'maater-proto-stack-nav'}-item-${node.id}`}
                onMouseEnter={(e) => {
                  if (!selected) {
                    e.currentTarget.style.background = 'var(--paper)'
                    e.currentTarget.style.color = 'var(--ink)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selected) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'var(--ink-soft)'
                  }
                }}
              >
                <span className="grid size-9 shrink-0 place-content-center">
                  {Icon ? <Icon className="h-4 w-4" /> : null}
                </span>
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] font-medium">
                  <span className="truncate">{node.label}</span>
                  {node.badge ? (
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium leading-5 tracking-[0.02em]"
                      style={
                        node.badge.tone === 'info'
                          ? {
                              background:
                                'color-mix(in oklch, oklch(55% 0.14 250) 14%, transparent)',
                              color: 'oklch(45% 0.14 250)',
                            }
                          : {
                              background: 'var(--paper-deep)',
                              color: 'var(--ink-soft)',
                            }
                      }
                    >
                      {node.badge.label}
                    </span>
                  ) : null}
                </span>
                {hasChildren ? (
                  <span
                    className="mr-1.5 grid size-6 shrink-0 place-content-center rounded-sm transition-colors"
                    style={{ color: 'var(--ink-faint)' }}
                  >
                    <ChevronRight className="h-3 w-3" />
                  </span>
                ) : null}
              </button>
            </div>
          )
        })}
      </nav>
    </div>
  )
}
